"use client"

import { useContext, useEffect, useMemo, useSyncExternalStore } from "react"
import { AuctionRealtimeContext } from "@/components/providers/auction-realtime-provider"
import type { AuctionSlot, AuctionChatItem } from "@/lib/auction-types"

interface UseAuctionOptions {
  initial?: Partial<AuctionSlot>
  broadcast?: boolean
}

/**
 * Subscribe to realtime auction state for a given listing.
 *
 * - On mount: registers the listing in the global provider map (subscribe)
 * - If `options.broadcast` is true: also subscribes to the on-demand broadcast
 *   channel for that listing (detail page use-case). RefCount ensures multiple
 *   instances on mobile share a single channel.
 * - Reactive reads via useSyncExternalStore — only re-renders when the slot
 *   for *this* listing changes.
 */
export function useAuction(
  listingId: string | null,
  options?: UseAuctionOptions
): AuctionSlot | null {
  const ctx = useContext(AuctionRealtimeContext)
  if (!ctx) throw new Error("useAuction must be used within AuctionRealtimeProvider")

  // Register listing on mount
  useEffect(() => {
    if (!listingId) return
    ctx.subscribe(listingId, options?.initial)
    return () => ctx.unsubscribe(listingId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId])

  // On-demand broadcast channel (detail page)
  useEffect(() => {
    if (!listingId || !options?.broadcast) return
    ctx.subscribeBroadcast(listingId)
    return () => ctx.unsubscribeBroadcast(listingId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, options?.broadcast])

  // Stable subscribe function for useSyncExternalStore
  const subscribe = useMemo(() => {
    return (onStoreChange: () => void) => {
      if (!listingId) return () => {}
      return ctx.addListener(listingId, onStoreChange)
    }
  }, [listingId, ctx])

  // Stable snapshot function
  const getSnapshot = useMemo(() => {
    return () => (listingId ? ctx.getSnapshot(listingId) : null)
  }, [listingId, ctx])

  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}

/**
 * Returns stable action callbacks scoped to a listing id.
 * Components that only dispatch (never read) can use this without re-rendering
 * on slot changes.
 */
export function useAuctionActions(listingId: string) {
  const ctx = useContext(AuctionRealtimeContext)
  if (!ctx) throw new Error("useAuctionActions must be within AuctionRealtimeProvider")

  return useMemo(
    () => ({
      patchAuction: (partial: Partial<AuctionSlot>) => ctx.patchAuction(listingId, partial),
      refetchBids: () => ctx.refetchBids(listingId),
      refetchChat: () => ctx.refetchChat(listingId),
      refetchSnapshot: () => ctx.refetchSnapshot(listingId),
      sendChatMessage: (item: AuctionChatItem) => ctx.sendChatMessage(listingId, item),
    }),
    // ctx is stable; listingId change recreates the actions object
    [listingId, ctx]
  )
}
